/**
 * provenance.ts — Item Provenance Assembly (Plan Spine)
 *
 * Answers "who agreed to this?" for one item on a published cycle report:
 * who ratified it and when, the evidence base behind it, where its values
 * came from, the request → proposal → acceptance chain that produced it,
 * the version chain (a plan has no edit button — supersession is always
 * shown, never hidden), and — when this item's own rank was changed by an
 * authorized override — who authorized that and why.
 *
 * Design rule (never guess): every field with no record on it degrades to
 * `null` (or an empty list), never a placeholder guess. A caller renders
 * `null` as "No record" — it never invents a name or a date.
 *
 * Data honesty: some prod tables carry fabricated future-dated fixture
 * rows. Every query below excludes rows whose `created_at` is more than 7
 * days in the future.
 *
 * This module only reads. It never writes, and it never imports a store —
 * see `src/pages/prioritization/CycleReportPage.tsx` (ProvenanceBar, lines
 * ~51-140), `src/stores/prioritization/cycleReportStore.ts` (history/version
 * fetches, ~403-464), and `src/components/prioritization/catchball/
 * CatchballExchangeTimeline.tsx` for the read-only shapes this was modeled
 * on.
 */

import { getTypedSupabaseClient } from '@/lib/typedSupabaseClient';
import type { TypedSupabaseClient } from '@/lib/typedSupabaseClient';

// ============================================================================
// Public result type
// ============================================================================

/** One resolved "who + when" fact. Both fields are `null` when there is no record. */
export interface ProvenanceActor {
  name: string | null;
  /** Raw ISO timestamp — callers format for display. */
  date: string | null;
}

export interface ItemProvenanceProposalChain {
  submittedBy: ProvenanceActor;
  acceptedBy: ProvenanceActor;
}

export interface ItemProvenanceVersionRef {
  version: number;
  /** Raw ISO timestamp of that version's ratification, or null if unrecorded. */
  date: string | null;
}

export interface ItemProvenanceVersion {
  version: number;
  /** Why this version exists (the report's own rationale text), or null. */
  changeReason: string | null;
  /** The earlier version this one replaced, if any. */
  replaces: ItemProvenanceVersionRef | null;
  /**
   * The later version that has since replaced this one, if any. Always
   * populated when true — a plan has no edit button, so supersession is
   * shown, never hidden.
   */
  replacedBy: ItemProvenanceVersionRef | null;
}

export interface ItemProvenanceOverride {
  previousRank: number | null;
  newRank: number | null;
  /** Written justification for the change, or null if none was recorded. */
  justification: string | null;
  authorizedBy: ProvenanceActor;
}

export interface ItemProvenance {
  itemId: string;
  itemTitle: string | null;
  cycleId: string;
  reportId: string;

  /** Who ratified this version of the plan, and when. */
  ratifiedBy: ProvenanceActor;

  /** Plain-language evidence-base line (e.g. "4 sessions across 2 rounds of input"), or null. */
  evidenceSummary: string | null;

  /** Plain-language description of where this plan's values came from, or null. */
  valueSource: string | null;

  /** The request → proposal → acceptance chain that produced this plan, or null if unrecorded. */
  proposalChain: ItemProvenanceProposalChain | null;

  version: ItemProvenanceVersion;

  /** Set only when this specific item's rank was changed by an authorized override. */
  override: ItemProvenanceOverride | null;

  /**
   * At most 4 plain-language, chronological lines summarizing the exchange
   * behind this plan (e.g. "Asked of the team by Jane Lee · Mar 3, 2026").
   * Empty when nothing is on record.
   */
  exchangeSummary: string[];

  /** True when at least one section above carries an actual record. */
  hasAnyRecord: boolean;
}

// ============================================================================
// Inputs — data the caller already has (a loaded cycle_reports row, mapped
// to camelCase the way the store already does) plus enough shape for us to
// find this one item inside it. We deliberately do NOT import CycleReport
// from the store — this is our own narrow, read-only view of the row.
// ============================================================================

export interface ProvenancePlanOfRecordItem {
  item_id: string;
  rank?: number | null;
  title?: string | null;
  override_applied?: boolean | null;
}

export interface ProvenancePlanOfRecord {
  session_count?: number | null;
  loop_count?: number | null;
  value_collection_snapshot_id?: string | null;
  ranked_items?: ProvenancePlanOfRecordItem[] | null;
}

export interface ProvenanceAmendmentLogEntry {
  item_id: string;
  previous_rank: number | null;
  new_rank: number | null;
  justification: string | null;
  authorized_by_id: string | null;
  authorized_at: string | null;
}

/** The subset of a `cycle_reports` row this module needs. Pass the report you already loaded straight through. */
export interface ProvenanceReportInput {
  id: string;
  cycleId: string;
  version: number;
  supersedesId?: string | null;
  ratifiedAt?: string | null;
  ratifiedBy?: string | null;
  rationale?: string | null;
  proposalId?: string | null;
  planOfRecord?: ProvenancePlanOfRecord | null;
  amendmentLog?: ProvenanceAmendmentLogEntry[] | null;
}

export interface AssembleProvenanceInputs {
  item: { id: string; title?: string | null };
  report: ProvenanceReportInput;
  /** Injectable for tests. Defaults to the shared typed Supabase client. */
  client?: TypedSupabaseClient;
}

// ============================================================================
// Internals
// ============================================================================

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuid(value: string): boolean {
  return UUID_REGEX.test(value);
}

/** Rows more than 7 days out from now are fabricated fixture data (see brief) — never trust them. */
function futureFixtureCutoffIso(): string {
  return new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
}

function nonEmpty(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

interface ProposalRow {
  loop_id: string | null;
  proposed_by: string | null;
  proposed_by_name: string | null;
  proposed_at: string | null;
  resolved_by: string | null;
  resolved_at: string | null;
}

interface LoopRow {
  request_issued_at: string | null;
  request_issued_by: string | null;
}

interface VersionRow {
  version: number;
  ratified_at: string | null;
}

async function fetchProposalRow(
  client: TypedSupabaseClient,
  proposalId: string,
  cutoffIso: string
): Promise<ProposalRow | null> {
  const { data, error } = await client
    .from('catchball_proposals')
    .select('loop_id, proposed_by, proposed_by_name, proposed_at, resolved_by, resolved_at')
    .eq('id', proposalId)
    .lte('created_at', cutoffIso)
    .maybeSingle();
  if (error || !data) return null;
  return data;
}

async function fetchLoopRow(
  client: TypedSupabaseClient,
  loopId: string,
  cutoffIso: string
): Promise<LoopRow | null> {
  const { data, error } = await client
    .from('catchball_loops')
    .select('request_issued_at, request_issued_by')
    .eq('id', loopId)
    .lte('created_at', cutoffIso)
    .maybeSingle();
  if (error || !data) return null;
  return data;
}

/** The version this report directly replaced (report.supersedesId), if any. */
async function fetchEarlierVersion(
  client: TypedSupabaseClient,
  supersedesId: string,
  cutoffIso: string
): Promise<VersionRow | null> {
  const { data, error } = await client
    .from('cycle_reports')
    .select('version, ratified_at')
    .eq('id', supersedesId)
    .lte('created_at', cutoffIso)
    .maybeSingle();
  if (error || !data) return null;
  return data;
}

/** The version that has since replaced this report (supersedes_id points back at us), if any. */
async function fetchLaterVersion(
  client: TypedSupabaseClient,
  reportId: string,
  cutoffIso: string
): Promise<VersionRow | null> {
  const { data, error } = await client
    .from('cycle_reports')
    .select('version, ratified_at')
    .eq('supersedes_id', reportId)
    .lte('created_at', cutoffIso)
    .order('version', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error || !data) return null;
  return data;
}

/**
 * Resolves user ids to display names using the SECURITY DEFINER RPC (so
 * RLS never blocks a cross-user name lookup — same reasoning as
 * `useActorDisplayMap`), falling back to a direct `users` table read (name,
 * then email) if the RPC is unavailable. Ids that resolve to nothing are
 * simply absent from the returned map — the caller renders that as "No
 * record", never a guess.
 */
async function resolveDisplayNames(
  client: TypedSupabaseClient,
  rawIds: Array<string | null | undefined>
): Promise<Map<string, string>> {
  const ids = Array.from(new Set(rawIds.filter((id): id is string => !!id && isUuid(id))));
  const resolved = new Map<string, string>();
  if (ids.length === 0) return resolved;

  const { data, error } = await client.rpc('resolve_user_display_names', { user_ids: ids });
  if (!error && data) {
    for (const row of data) {
      const name = nonEmpty(row.display_name);
      if (row.id && name) resolved.set(row.id, name);
    }
    return resolved;
  }

  const { data: fallbackRows } = await client.from('users').select('id, name, email').in('id', ids);
  for (const row of fallbackRows ?? []) {
    const name = nonEmpty(row.name) ?? nonEmpty(row.email);
    if (name) resolved.set(row.id, name);
  }
  return resolved;
}

/** A raw actor id may be a resolvable UUID, or already a free-text name captured at the time (legacy rows) — either degrades honestly to null, never a guess. */
function actorName(rawId: string | null | undefined, names: Map<string, string>): string | null {
  if (!rawId) return null;
  if (!isUuid(rawId)) return nonEmpty(rawId);
  return names.get(rawId) ?? null;
}

function shortDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  // Fixed to UTC — these are ratification/proposal timestamps recorded once,
  // shared across every viewer's timezone. Using the browser's local zone
  // would shift the displayed day near midnight (a real "Mar 1" could read
  // as "Feb 28" for a viewer behind UTC) and made this non-deterministic in
  // tests depending on the runner's TZ.
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function formatEvidenceSummary(sessionCount: number, roundCount: number): string {
  const sessions = `${sessionCount} session${sessionCount === 1 ? '' : 's'}`;
  const rounds = `${roundCount} round${roundCount === 1 ? '' : 's'} of input`;
  return `${sessions} across ${rounds}`;
}

// ============================================================================
// assembleProvenance
// ============================================================================

export async function assembleProvenance(inputs: AssembleProvenanceInputs): Promise<ItemProvenance> {
  const { item, report } = inputs;
  const client = inputs.client ?? getTypedSupabaseClient();
  const cutoffIso = futureFixtureCutoffIso();

  const proposalRow = report.proposalId
    ? await fetchProposalRow(client, report.proposalId, cutoffIso)
    : null;

  const loopRow = proposalRow?.loop_id
    ? await fetchLoopRow(client, proposalRow.loop_id, cutoffIso)
    : null;

  const earlierVersion = report.supersedesId
    ? await fetchEarlierVersion(client, report.supersedesId, cutoffIso)
    : null;

  const laterVersion = await fetchLaterVersion(client, report.id, cutoffIso);

  // This item's own row inside the plan, if the caller supplied ranked_items.
  const itemEntry =
    report.planOfRecord?.ranked_items?.find((r) => r.item_id === item.id) ?? null;

  // The strongest "who agreed to THIS item" signal: a per-item amendment
  // entry with an explicit authorizer. Falls back to the plain override
  // flag on the item's own plan-of-record row (still true data, just
  // without an attributed authorizer on record) — never a guess either way.
  const itemAmendment =
    report.amendmentLog?.find((entry) => entry.item_id === item.id) ?? null;

  const namesToResolve: Array<string | null | undefined> = [
    report.ratifiedBy,
    proposalRow?.proposed_by,
    proposalRow?.resolved_by,
    loopRow?.request_issued_by,
    itemAmendment?.authorized_by_id,
  ];
  const names = await resolveDisplayNames(client, namesToResolve);

  // ---- Ratification ---------------------------------------------------
  const ratifiedBy: ProvenanceActor = {
    name: actorName(report.ratifiedBy, names),
    date: report.ratifiedAt ?? null,
  };

  // ---- Evidence base + value source ------------------------------------
  const por = report.planOfRecord ?? null;
  const evidenceSummary =
    por && (por.session_count != null || por.loop_count != null)
      ? formatEvidenceSummary(por.session_count ?? 0, por.loop_count ?? 0)
      : null;

  const valueSource = por
    ? por.value_collection_snapshot_id
      ? `A saved set of values, snapshot ${por.value_collection_snapshot_id.slice(0, 8)}…`
      : "The workspace's standing values"
    : null;

  // ---- Proposal chain -----------------------------------------------------
  const proposalChain: ItemProvenanceProposalChain | null = proposalRow
    ? {
        submittedBy: {
          name: nonEmpty(proposalRow.proposed_by_name) ?? actorName(proposalRow.proposed_by, names),
          date: proposalRow.proposed_at ?? null,
        },
        acceptedBy: {
          name: actorName(proposalRow.resolved_by, names),
          date: proposalRow.resolved_at ?? null,
        },
      }
    : null;

  // ---- Version chain (never hidden) ----------------------------------------
  const version: ItemProvenanceVersion = {
    version: report.version,
    changeReason: nonEmpty(report.rationale),
    replaces:
      report.supersedesId && earlierVersion
        ? { version: earlierVersion.version, date: earlierVersion.ratified_at ?? null }
        : null,
    replacedBy: laterVersion ? { version: laterVersion.version, date: laterVersion.ratified_at ?? null } : null,
  };

  // ---- Per-item override -----------------------------------------------
  const override: ItemProvenanceOverride | null = itemAmendment
    ? {
        previousRank: itemAmendment.previous_rank,
        newRank: itemAmendment.new_rank,
        justification: nonEmpty(itemAmendment.justification),
        authorizedBy: {
          name: actorName(itemAmendment.authorized_by_id, names),
          date: itemAmendment.authorized_at,
        },
      }
    : itemEntry?.override_applied
      ? {
          previousRank: null,
          newRank: itemEntry.rank ?? null,
          justification: null,
          authorizedBy: { name: null, date: null },
        }
      : null;

  // ---- Exchange summary — at most 4 plain-language, chronological lines ---
  const exchangeSummary: string[] = [];

  const askerName = actorName(loopRow?.request_issued_by, names);
  const askDate = shortDate(loopRow?.request_issued_at);
  if (askerName || askDate) {
    exchangeSummary.push(
      `Asked of the team${askerName ? ` by ${askerName}` : ''}${askDate ? ` · ${askDate}` : ''}`
    );
  }

  const proposedDate = shortDate(proposalRow?.proposed_at);
  if (proposedDate) {
    exchangeSummary.push(`The team proposed this ranking · ${proposedDate}`);
  }

  const acceptedName = actorName(proposalRow?.resolved_by, names);
  const acceptedDate = shortDate(proposalRow?.resolved_at);
  if (acceptedName || acceptedDate) {
    exchangeSummary.push(
      `Accepted${acceptedName ? ` by ${acceptedName}` : ''}${acceptedDate ? ` · ${acceptedDate}` : ''}`
    );
  }

  if (override) {
    const overrideDate = shortDate(override.authorizedBy.date);
    exchangeSummary.push(
      `This item's rank was changed${override.authorizedBy.name ? ` by ${override.authorizedBy.name}` : ''}${overrideDate ? ` · ${overrideDate}` : ''}`
    );
  }

  const itemTitle = nonEmpty(item.title) ?? nonEmpty(itemEntry?.title) ?? null;

  const hasAnyRecord = Boolean(
    ratifiedBy.name ||
      ratifiedBy.date ||
      evidenceSummary ||
      valueSource ||
      proposalChain ||
      version.replaces ||
      version.replacedBy ||
      override ||
      exchangeSummary.length > 0
  );

  return {
    itemId: item.id,
    itemTitle,
    cycleId: report.cycleId,
    reportId: report.id,
    ratifiedBy,
    evidenceSummary,
    valueSource,
    proposalChain,
    version,
    override,
    exchangeSummary: exchangeSummary.slice(0, 4),
    hasAnyRecord,
  };
}
